<template>
  <UIButton
    v-if="nextLender"
    id="continueTopoff"
    :label="action.label"
    :disabled="continueDisabled"
    @click="open"
  />
  <UIModal
    v-model:visible="visible"
    :accept-button-label="t('actions.agree_continue')"
    :cancel-button-label="t('actions.exit')"
    aria-labelledby="topoff-title"
    aria-describedby="topoff-content"
    @modal-accept="next"
    @modal-reject="reject"
  >
    <template #header>
      <i18n-t
        id="topoff-title"
        keypath="title"
        tag="h2"
        class="text-base! font-bold"
      />
    </template>
    <div id="topoff-content" class="space-y-4">
      <i18n-t keypath="consent[0]" tag="p" />

      <i18n-t keypath="consent[1]" tag="p">
        <!--suppress VueUnrecognizedSlot -->
        <template #action>
          <i18n-t keypath="actions.agree_continue" />
        </template>
      </i18n-t>

      <i18n-t keypath="back_out" tag="p">
        <!--suppress VueUnrecognizedSlot -->
        <template #action>
          <i18n-t keypath="actions.exit" />
        </template>
      </i18n-t>
    </div>
  </UIModal>
</template>

<script setup lang="ts">
  import { useProviderEndAction, useTransitionToNextLender } from "@/composition"
  import type {
    CascadeResponse,
    ProviderKey,
  } from "@versatile-credit-apply/omni-apply-client"
  import { useProviderI18n } from "@versatile-credit-apply/sdk"

  interface Props {
    providerKey: ProviderKey
    nextLender: CascadeResponse
    continueDisabled?: boolean
  }

  const props = defineProps<Props>()
  const action = useProviderEndAction({
    type: "topoff"
  })

  const visible = defineModel<boolean>({
    default: false,
  })

  const open = () => {
    visible.value = true
  }

  const reject = () => {
    visible.value = false
  }
  const next = useTransitionToNextLender(
    () => props.nextLender?.provider?.providerKey,
    () => props.nextLender?.applyMode
  )
  const { t } = useProviderI18n(() => props.providerKey)
</script>

<i18n lang="yaml" locale="en">
back_out: |
  Click "{action}" if you do not consent to share your information for additional
    consideration at this time.
consent:
  - You have the opportunity to be considered for an additional account from a
    separate lender and/or lease purchase provider to make an additional purchase.

  - By clicking "{action}" I consent to share my application
    information, including income, with one or more separate lender and/or lease
    purchase providers so I can be considered for an additional account. I
    understand this may result in one or more additional credit inquiries and
    may result in an additional credit card. I understand that rates, fees, and
    other credit terms may vary from provider to provider, and I will review and
    obtain copies of such terms through this process.
merchant: |
  {merchantName} is providing you the opportunity to be considered for a second line of credit
  from a separate lender to make an additional purchase.
title: Would you like to be considered for a second line of credit?
</i18n>

<i18n lang="yaml" locale="synchrony_standard_en">
merchant: |
  {merchantName} is providing you the opportunity to be considered for an additional account from
  a separate lender and/or lease purchase provider to make an additional purchase.
title: Would you like to be considered for an additional account?
</i18n>
